import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { InputProps } from './Input.types';
import { InputContainer } from '../InputContainer/InputContainer';
import { TooltipIcon } from '../TooltipIcon';
import { formatCurrency } from '../../util/string.util';
import { Icon } from '@stax-core/truffle-components';

const InputIcon = styled.i`
  position: absolute;
  right: 0.8rem;
  top: 34%;
`;

export const Input: FC<InputProps> = props => {
  const {
    id,
    value,
    label,
    icon,
    tooltip,
    className,
    active: parentActive,
    currency,
    customIcon,
    ...rest
  } = props;

  let formattedValue = value;

  if (currency) {
    formattedValue = formatCurrency(value ? value.replace(/[^0-9.]/g, '') : 0);
  }

  const [active, setActive] = useState(props.active || !!value);
  const inputField = useRef<HTMLInputElement>(null);

  const makeActive = useCallback(
    (value: boolean) => {
      if (typeof parentActive === 'boolean') {
        setActive(parentActive);
      } else {
        setActive(value);
      }
    },
    [parentActive],
  );

  useEffect(() => {
    if ((value || formattedValue) && !active) {
      makeActive(true);
    }
  }, [value, active, makeActive, formattedValue]);

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    makeActive(true);
    if (props.onFocus) props.onFocus(e);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    makeActive(!!e.target.value);
    if (props.onBlur) props.onBlur(e);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currency && e.target.value) {
      e.target.value = String(+e.target.value.replace(/[^0-9]/g, '') / 100);
    }

    if (props.onChange) props.onChange(e);
  };

  const renderIcon = icon => {
    return (
      <InputIcon
        style={{
          top: label
            ? '54%'
            : '34%' /* because the label is conditional and adds height to the parent div */,
        }}
        onClick={evt => {
          inputField.current?.select();
          inputField.current?.focus();
        }}
      >
        {icon}
      </InputIcon>
    );
  };
  return (
    <InputContainer
      className={`ui-input ${active ? '-active' : ''} ${
        icon ? 'has-icon' : ''
      } ${className ?? ''} `}
    >
      <>
        {label && (
          <div>
            <label htmlFor={id}>{label}</label>
          </div>
        )}
        {(icon || customIcon) &&
          renderIcon(customIcon ?? <Icon icon={icon!} from={'fa'} />)}

        {tooltip && <TooltipIcon label={label} tooltip={tooltip} />}
        <input
          {...rest}
          id={id}
          data-testid={id}
          className={className}
          value={formattedValue}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          ref={inputField}
        />
      </>
    </InputContainer>
  );
};

Input.defaultProps = {
  type: 'text',
};
